/**
 * Tenant Context - Multi-Tenant, Multi-Retailer & DTC Platform State Management
 * 
 * Manages:
 * 1. Current user's company/tenant
 * 2. Available retailers for this company (brick & mortar)
 * 3. Available DTC platforms (Shopify, Amazon)
 * 4. Selected retailer/platform filters
 * 5. URL synchronization
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '../lib/api-client';
import { 
  getRetailerCapabilities, 
  isPageSupportedForRetailer,
  type RetailerCapabilities 
} from '../lib/retailer-capabilities';

// ============================================================================
// TYPES
// ============================================================================

export interface Retailer {
  id: string;
  name: string;
  slug: string;
  type: 'brick_mortar' | 'ecommerce' | 'hybrid' | 'dtc_shopify' | 'dtc_amazon';
  logo_url?: string | null;
  is_active?: boolean;
  created_at?: string;
}

export interface DtcPlatform {
  id: string;
  platform_type: 'shopify' | 'amazon_seller_central';
  platform_name: string;
  is_active: boolean;
  last_sync_at: string | null;
  sync_status: 'pending' | 'syncing' | 'success' | 'error';
}

interface TenantContextType {
  // Company info
  companyId: string | null;
  companyName: string | null;
  
  // Available retailers for this company (brick & mortar only)
  retailers: Retailer[];
  isLoadingRetailers: boolean;
  retailersError: Error | null;
  
  // Currently selected retailer (null = all retailers, aggregated view)
  selectedRetailerId: string | null;
  setSelectedRetailerId: (id: string | null) => void;
  
  // DTC Platforms (Shopify, Amazon)
  dtcPlatforms: DtcPlatform[];
  isLoadingDtcPlatforms: boolean;
  dtcPlatformsError: Error | null;
  
  // Currently selected DTC platform (null = none selected)
  selectedDtcPlatformId: string | null;
  setSelectedDtcPlatformId: (id: string | null) => void;
  
  // Utility functions
  getRetailerById: (id: string) => Retailer | undefined;
  getRetailerBySlug: (slug: string) => Retailer | undefined;
  getDtcPlatformById: (id: string) => DtcPlatform | undefined;
  isMultiRetailer: boolean; // True if company has > 1 retailer
  hasDtcPlatforms: boolean; // True if company has any DTC platforms
  selectedRetailer: Retailer | null; // Currently selected retailer object
  selectedDtcPlatform: DtcPlatform | null; // Currently selected DTC platform
  
  // Retailer Capabilities
  currentRetailerCapabilities: RetailerCapabilities; // Capabilities for selected retailer
  isPageAvailableForRetailer: (pageSlug: string) => boolean; // Check if page should be visible
  
  // Refresh lists
  refetchRetailers: () => Promise<void>;
  refetchDtcPlatforms: () => Promise<void>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  
  // Company State (fetched from API)
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  
  // Retailer State
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [isLoadingRetailers, setIsLoadingRetailers] = useState(true);
  const [retailersError, setRetailersError] = useState<Error | null>(null);
  const [selectedRetailerId, setSelectedRetailerId] = useState<string | null>(null);
  const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);

  // DTC Platform State
  const [dtcPlatforms, setDtcPlatforms] = useState<DtcPlatform[]>([]);
  const [isLoadingDtcPlatforms, setIsLoadingDtcPlatforms] = useState(true);
  const [dtcPlatformsError, setDtcPlatformsError] = useState<Error | null>(null);
  const [selectedDtcPlatformId, setSelectedDtcPlatformId] = useState<string | null>(null);

  // ============================================================================
  // FETCH COMPANY ID
  // ============================================================================

  const fetchCompanyId = async () => {
    if (!isAuthenticated || !user) {
      setCompanyId(null);
      setIsLoadingCompany(false);
      return;
    }

    try {
      setIsLoadingCompany(true);
      console.log('[TenantContext] Fetching company ID for user:', user.email);
      const data = await apiClient.get<{ companyId: string; role: string }>('/user/company');
      console.log('[TenantContext] Fetched company ID:', data.companyId);
      setCompanyId(data.companyId);
    } catch (error) {
      console.error('[TenantContext] Error fetching company ID:', error);
      // Fallback to a default company ID for now
      setCompanyId('00000000-0000-0000-0000-000000000001');
    } finally {
      setIsLoadingCompany(false);
    }
  };

  useEffect(() => {
    fetchCompanyId();
  }, [isAuthenticated, user?.email]);

  // ============================================================================
  // FETCH RETAILERS (Brick & Mortar)
  // ============================================================================

  const fetchRetailers = async () => {
    if (!isAuthenticated || !user) {
      setRetailers([]);
      setIsLoadingRetailers(false);
      return;
    }

    try {
      setIsLoadingRetailers(true);
      setRetailersError(null);
      
      console.log('[TenantContext] Fetching retailers for user:', user.email);
      const data = await apiClient.get<Retailer[]>('/user/retailers');
      
      // Filter out DTC platforms (they have type starting with 'dtc_')
      const brickAndMortarRetailers = (data || []).filter(
        r => !r.type.startsWith('dtc_')
      );
      
      console.log(`[TenantContext] Fetched ${brickAndMortarRetailers.length} brick & mortar retailers`);
      setRetailers(brickAndMortarRetailers);
      
    } catch (error) {
      console.error('[TenantContext] Error fetching retailers:', error);
      setRetailersError(error as Error);
      setRetailers([]);
    } finally {
      setIsLoadingRetailers(false);
    }
  };

  // ============================================================================
  // FETCH DTC PLATFORMS
  // ============================================================================

  const fetchDtcPlatforms = async () => {
    if (!isAuthenticated || !user) {
      setDtcPlatforms([]);
      setIsLoadingDtcPlatforms(false);
      return;
    }

    try {
      setIsLoadingDtcPlatforms(true);
      setDtcPlatformsError(null);
      
      console.log('[TenantContext] Fetching DTC platforms for user:', user.email);
      const data = await apiClient.get<DtcPlatform[]>('/dtc/platforms');
      
      console.log(`[TenantContext] Fetched ${data?.length || 0} DTC platforms`);
      setDtcPlatforms(data || []);
      
    } catch (error) {
      console.error('[TenantContext] Error fetching DTC platforms:', error);
      setDtcPlatformsError(error as Error);
      setDtcPlatforms([]);
    } finally {
      setIsLoadingDtcPlatforms(false);
    }
  };

  // Fetch retailers and DTC platforms when user logs in
  useEffect(() => {
    fetchRetailers();
    fetchDtcPlatforms();
  }, [user, isAuthenticated]);

  // ============================================================================
  // URL SYNCHRONIZATION
  // ============================================================================

  // Sync selectedRetailerId from URL on mount (ONLY ONCE when retailers load)
  useEffect(() => {
    // Only run once when retailers are first loaded
    if (retailers.length === 0 || hasInitializedFromUrl) return;

    const params = new URLSearchParams(window.location.search);
    const retailerSlug = params.get('retailer');
    
    console.log(`[TenantContext] Initializing from URL, slug: ${retailerSlug}`);
    
    if (retailerSlug) {
      // Find retailer by slug
      console.log(`[TenantContext] Searching for slug "${retailerSlug}" in ${retailers.length} retailers:`, retailers.map(r => r.slug));
      const retailer = retailers.find(r => r.slug === retailerSlug);
      if (retailer) {
        console.log(`[TenantContext] Setting retailer from URL: ${retailer.name} (${retailer.id})`);
        setSelectedRetailerId(retailer.id);
      } else {
        console.warn(`[TenantContext] Retailer not found for slug: ${retailerSlug}. Available: ${retailers.map(r => r.slug).join(', ')}`);
        setSelectedRetailerId(null);
      }
    } else {
      console.log('[TenantContext] No retailer in URL, defaulting to Overview');
      setSelectedRetailerId(null);
    }
    
    setHasInitializedFromUrl(true);
  }, [retailers, hasInitializedFromUrl]);

  // Update URL when selectedRetailerId changes
  const handleSetSelectedRetailerId = (id: string | null) => {
    setSelectedRetailerId(id);
    
    // Update URL
    const url = new URL(window.location.href);
    if (id) {
      const retailer = retailers.find(r => r.id === id);
      if (retailer) {
        url.searchParams.set('retailer', retailer.slug);
        console.log(`[TenantContext] Updating URL with retailer: ${retailer.slug}`);
      }
    } else {
      url.searchParams.delete('retailer');
      console.log('[TenantContext] Clearing retailer from URL');
    }
    
    // Use pushState to update URL without page reload
    window.history.pushState({}, '', url.toString());
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const getRetailerById = (id: string): Retailer | undefined => {
    return retailers.find(r => r.id === id);
  };

  const getRetailerBySlug = (slug: string): Retailer | undefined => {
    return retailers.find(r => r.slug === slug);
  };

  const getDtcPlatformById = (id: string): DtcPlatform | undefined => {
    return dtcPlatforms.find(p => p.id === id);
  };

  const selectedRetailer = selectedRetailerId 
    ? getRetailerById(selectedRetailerId) || null
    : null;

  const selectedDtcPlatform = selectedDtcPlatformId
    ? getDtcPlatformById(selectedDtcPlatformId) || null
    : null;

  const isMultiRetailer = retailers.length > 1;
  const hasDtcPlatforms = dtcPlatforms.length > 0;

  // ============================================================================
  // RETAILER CAPABILITIES
  // ============================================================================

  // Get capabilities for currently selected retailer
  const currentRetailerCapabilities = getRetailerCapabilities(selectedRetailerId);

  // Check if a page is available for the current retailer
  const isPageAvailableForRetailer = (pageSlug: string): boolean => {
    return isPageSupportedForRetailer(selectedRetailerId, pageSlug);
  };

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: TenantContextType = {
    // Company info (fetched from API)
    companyId: companyId,
    companyName: 'Buttah Skin', // TODO: Get from API

    // Retailers (Brick & Mortar)
    retailers,
    isLoadingRetailers,
    retailersError,
    selectedRetailerId,
    setSelectedRetailerId: handleSetSelectedRetailerId,
    
    // DTC Platforms
    dtcPlatforms,
    isLoadingDtcPlatforms,
    dtcPlatformsError,
    selectedDtcPlatformId,
    setSelectedDtcPlatformId,
    
    // Utilities
    getRetailerById,
    getRetailerBySlug,
    getDtcPlatformById,
    isMultiRetailer,
    hasDtcPlatforms,
    selectedRetailer,
    selectedDtcPlatform,
    
    // Retailer Capabilities
    currentRetailerCapabilities,
    isPageAvailableForRetailer,
    
    // Actions
    refetchRetailers: fetchRetailers,
    refetchDtcPlatforms: fetchDtcPlatforms,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access tenant context
 * 
 * @example
 * const { selectedRetailerId, retailers, setSelectedRetailerId } = useTenant();
 * 
 * // Check if viewing all retailers
 * if (!selectedRetailerId) {
 *   // Master dashboard (aggregated view)
 * }
 * 
 * // Get selected retailer details
 * const retailer = selectedRetailer;
 * console.log(retailer?.name); // "Ulta Beauty"
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}

// ============================================================================
// FEATURE FLAG (for gradual rollout)
// ============================================================================

/**
 * Check if multi-retailer feature is enabled
 * Can be controlled via environment variable
 */
export function isMultiRetailerEnabled(): boolean {
  return import.meta.env.VITE_MULTI_RETAILER !== 'false';
}

